/* eslint-disable max-depth */
import React, { useContext, useMemo, ComponentType, ReactNode } from 'react';
import { ApolloError, useQuery } from '@apollo/client';

import {
  useGetMeQuery,
  GetMeQuery,
  Maybe,
  Permission,
  ResourceCostModeType
} from '~/types';
import {
  PTI_INTROSPECTION_QUERY,
  DTM_SESSION_METADATA_QUERY
} from '~/modules/common/hooks/useIsPtiEnabled';

import { LinearQueryProgress } from '~/modules/common';
import { RedirectToUnhandledError } from '~/modules/common/unhandled-error';

import { MeContext, MeType } from './context';

export const injectMe = ({
  Loading = LinearQueryProgress,
  Error = RedirectToUnhandledError,
  SpecificErrorHandlers = []
}: {
  Loading?: ComponentType;
  Error?: ComponentType;
  SpecificErrorHandlers?: {
    predicate: (error: ApolloError | undefined) => boolean;
    Component: ComponentType;
  }[];
} = {}) => (Component: ComponentType) => (props: any): ReactNode => {
  const {
    loading,
    error,
    data: { me = {} as Maybe<GetMeQuery['me']> } = {}
  } = useGetMeQuery();

  // PTI feature flag check
  const isPsaDtmRequisitionEnabled = Boolean(
    me?.featureFlags?.isPsaDtmRequisitionEnabled
  );

  // PTI introspection query
  const { loading: enabledLoading, data: enabledData } = useQuery(
    PTI_INTROSPECTION_QUERY,
    {
      skip: !isPsaDtmRequisitionEnabled
    }
  );

  // PTI session metadata query
  const { loading: sessionLoading, data: sessionData } = useQuery(
    DTM_SESSION_METADATA_QUERY,
    {
      skip:
        !isPsaDtmRequisitionEnabled ||
        enabledLoading ||
        !enabledData?.__type?.name
    }
  );

  const memoMe: Maybe<MeType> = useMemo(
    () =>
      me
        ? {
            ...me,
            permissionsMap: (me.permissions || []).reduce(
              (result: any, current: any) => {
                if (current?.permissionActionUri) {
                  return {
                    ...result,
                    [current.permissionActionUri]: current as Permission
                  };
                }

                return result;
              },
              {}
            ) as { [key: string]: Permission },
            tenantPermissions: {
              isProjectLeaderApprovalEnabled: Boolean(
                !me?.featureFlags?.isPsaRmpStandaloneResourcingModuleEnabled ||
                  me?.hasApproveRejectTimesheet ||
                  me?.hasApproveRejectExpense
              ),
              isEditTimesheetEnabled: Boolean(
                !me?.featureFlags?.isPsaRmpStandaloneResourcingModuleEnabled ||
                  me?.hasEditTimesheet
              )
            },
            isPtiEnabled: Boolean(
              isPsaDtmRequisitionEnabled &&
                !enabledLoading &&
                !sessionLoading &&
                sessionData?.getDtmSessionMetadata?.length
            ),
            isRoleCostMode:
              !me?.featureFlags?.isPsaPrpTcop2Enabled &&
              me?.resourceCostMode === ResourceCostModeType.Rolecost
          }
        : null,
    [
      me,
      isPsaDtmRequisitionEnabled,
      enabledLoading,
      sessionLoading,
      sessionData
    ]
  );
  const isPtiEnabledCheckLoading =
    isPsaDtmRequisitionEnabled && (enabledLoading || sessionLoading);

  if (loading || isPtiEnabledCheckLoading) {
    return <Loading />;
  }

  if (error || !me) {
    for (const handler of SpecificErrorHandlers) {
      if (handler.predicate(error)) {
        return <handler.Component />;
      }
    }

    return <Error />;
  }

  return (
    <MeContext.Provider value={memoMe}>
      <Component {...props} me={memoMe} />
    </MeContext.Provider>
  );
};

const withMe = () => (Component: ComponentType) => (props: any): ReactNode => {
  const me = useContext(MeContext);

  return <Component {...props} me={me} />;
};

export default withMe;
